import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, Copy, Sparkles, FileText, Clock, CheckCircle, AlertCircle, Tag, MessageSquare, Settings, Folder, FolderOpen, Image as ImageIcon, Heart, MoreVertical, ZoomIn, Pencil, Check, X as XIcon, ExternalLink, Share2, ChevronDown, Link2, Focus, BookOpen, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import contentService from '../../services/contentService';
import topicService from '../../services/topicService';
import itemsService from '../../services/itemsService';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import ProgressBar from '../ui/ProgressBar';
import { getThumbnailUrl } from '../../utils/thumbnailFallbacks';
import { getImageUrl } from '../../utils/imageProxy';
import RelatedItemsRow from '../related-items/RelatedItemsRow';
import { useAskSwirlStore } from '../../stores/askSwirlStore';
import ReadingModeModal from './ReadingModeModal';
import processingQueue from '../../utils/processingQueue';

// Decode HTML entities in strings (e.g., &quot; -> ", &#x1f937; -> 🤷)
function decodeHtmlEntities(input) {
  if (!input || typeof input !== 'string') return input;
  let s = input
    .replaceAll('&quot;', '"')
    .replaceAll('&apos;', "'")
    .replaceAll('&#39;', "'")
    .replaceAll('&amp;', '&')
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>');
  // Handle hex character references like &#x1f937;
  s = s.replace(/&#x([0-9a-fA-F]+);/g, (_m, hex) => {
    try {
      return String.fromCodePoint(parseInt(hex, 16));
    } catch {
      return _m;
    }
  });
  // Handle decimal character references like &#8230;
  s = s.replace(/&#(\d+);/g, (_m, dec) => {
    try {
      return String.fromCodePoint(parseInt(dec, 10));
    } catch {
      return _m;
    }
  });
  return s;
}

const ContentDetailModal = ({ isOpen, onClose, content }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addChip, clearChips } = useAskSwirlStore();
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [currentContent, setCurrentContent] = useState(content);
  const [collections, setCollections] = useState([]);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [currentCollection, setCurrentCollection] = useState(null);
  const [showCollectionSelector, setShowCollectionSelector] = useState(false);
  const [changingCollection, setChangingCollection] = useState(false);
  const collectionButtonRef = useRef(null);
  const [relatedItems, setRelatedItems] = useState([]);
  const [loadingRelatedItems, setLoadingRelatedItems] = useState(false);
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [showDeleteMenu, setShowDeleteMenu] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [collectionChangeSuccess, setCollectionChangeSuccess] = useState(false);
  const [askSwirlPrompt, setAskSwirlPrompt] = useState('');
  const [showReadingMode, setShowReadingMode] = useState(false);
  // Carousel state
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Update currentContent when content prop changes
  useEffect(() => {
    // Reset carousel index when content changes
    setCarouselIndex(0);
    
    const loadFullContent = async () => {
      if (!content) return;
      
      // ALWAYS fetch full data to ensure all metadata (colors, tags, etc.) is available
      // This is needed because search results may not include all fields
      if (content.id) {
        try {
          const { data: fullItem, error } = await supabase
            .schema('deepsearch')
            .from('items')
            .select('*, meta')
            .eq('id', content.id)
            .single();
          
          if (!error && fullItem) {
            console.log('[ContentModal] Fetched full item with meta:', {
              id: fullItem.id,
              hasMeta: !!fullItem.meta,
              metaKeys: fullItem.meta ? Object.keys(fullItem.meta) : []
            });
            
            // Apply the same mapping logic as handleRelatedItemClick
            const meta = fullItem.meta || {};
            const channel = meta.channel || content.channel || null;
            const contentType = meta.content_type || null;
            const transcription = meta.transcription || null;
            
            // Determine actual source type: if transcription exists, it's a video
            let actualSourceType = fullItem.source_type;
            if (transcription && (transcription.full_transcript || transcription.text || transcription.transcript)) {
              actualSourceType = 'video';
            } else if (channel === 'YouTube' || channel === 'TikTok') {
              actualSourceType = 'video';
            } else if (channel === 'Instagram' && (contentType === 'reel' || contentType === 'video')) {
              actualSourceType = 'video';
            }
            
            const mappedContent = {
              ...fullItem,
              id: fullItem.id,
              title: fullItem.custom_title || fullItem.title || fullItem.description || 'Untitled',
              custom_title: fullItem.custom_title,
              description: fullItem.description,
              url: fullItem.url,
              image_url: fullItem.image_url,
              content_type: actualSourceType,
              source_type: actualSourceType,
              channel: channel,
              meta: meta, // Include meta for getThumbnailUrl
              transcription: transcription,
              analysis_data: meta.analysis_data || {
                // Books often won't have ai_analysis; fall back to description so Summary is always useful
                summary: meta.ai_analysis?.summary || meta.bookDescription || fullItem.description || null,
                ocr: {
                  text: fullItem.raw_text || fullItem.extracted_text,
                  hasText: !!fullItem.raw_text,
                  confidence: fullItem.text_confidence,
                },
                colors: {
                  primaryColor: fullItem.primary_color,
                  mood: fullItem.color_mood,
                  palette: fullItem.color_palette,
                },
                tagging: {
                  primaryTags: fullItem.primary_tags,
                  allTags: fullItem.auto_tags,
                },
              },
              extracted_text: fullItem.raw_text || fullItem.extracted_text,
              auto_tags: fullItem.auto_tags,
              primary_tags: fullItem.primary_tags,
              primary_color: fullItem.primary_color,
              color_mood: fullItem.color_mood,
              color_palette: fullItem.color_palette,
              _source: 'deepsearch.items',
              embedding_text: fullItem.embedding_text,
              raw_text: fullItem.raw_text,
            };
            
            // Now getThumbnailUrl will have access to full meta fields
            const rawThumbnailUrl = getThumbnailUrl(mappedContent);
            if (rawThumbnailUrl) {
              mappedContent.thumbnail_url = rawThumbnailUrl;
              console.log('[ContentModal] Resolved thumbnail:', rawThumbnailUrl.substring(0, 80));
            } else {
              console.warn('[ContentModal] No thumbnail found even with full meta');
            }
            
            setCurrentContent(mappedContent);
            setActiveTab('summary');
            return;
          }
        } catch (err) {
          console.error('[ContentModal] Error fetching from deepsearch.items:', err);
          // Try instagram_content as fallback
        }
        
        // Try instagram_content if not found in deepsearch.items
        try {
          const { data: instagramItem, error: instagramError } = await supabase
            .schema('public')
            .from('instagram_content')
            .select('*')
            .eq('id', content.id)
            .single();
          
          if (!instagramError && instagramItem) {
            console.log('[ContentModal] Fetched from instagram_content:', instagramItem.id);
            const mappedContent = {
              ...instagramItem,
              id: instagramItem.id,
              title: instagramItem.title || instagramItem.description || 'Untitled',
              description: instagramItem.description,
              url: instagramItem.url,
              image_url: instagramItem.image_url,
              thumbnail_url: instagramItem.thumbnail_url,
              source_type: instagramItem.content_type || 'post',
              content_type: instagramItem.content_type,
              channel: instagramItem.channel,
              auto_tags: instagramItem.auto_tags,
              primary_tags: instagramItem.primary_tags,
              primary_color: instagramItem.primary_color,
              color_mood: instagramItem.color_mood,
              color_palette: instagramItem.color_palette,
              analysis_data: instagramItem.analysis_data,
              transcription: instagramItem.transcription,
              _source: 'instagram_content',
            };
            
            const rawThumbnailUrl = getThumbnailUrl(mappedContent);
            if (rawThumbnailUrl) {
              mappedContent.thumbnail_url = rawThumbnailUrl;
            }
            
            setCurrentContent(mappedContent);
            setActiveTab('summary');
            return;
          }
        } catch (instErr) {
          console.error('[ContentModal] Error fetching from instagram_content:', instErr);
        }
      }
      
      // Fallback: use original content if all fetches failed
      const mappedContent = { ...content };
      if (mappedContent.custom_title) {
        mappedContent.title = mappedContent.custom_title;
      }
      const rawThumbnailUrl = getThumbnailUrl(content);
      if (rawThumbnailUrl) {
        mappedContent.thumbnail_url = rawThumbnailUrl;
      }
      setCurrentContent(mappedContent);
      setActiveTab('summary');
    };
    
    loadFullContent();
  }, [content]);

  // Load favorite status when content changes
  useEffect(() => {
    const loadFavoriteStatus = async () => {
      if (!currentContent?.id || !user?.id) {
        setIsFavorited(false);
        return;
      }

      try {
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/d63f98f2-a476-45f2-8f18-652bfd36f226',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ContentDetailModal.jsx:244',message:'loadFavoriteStatus entry',data:{userId:user?.id,itemId:currentContent?.id},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'B'})}).catch(()=>{});
        // #endregion
        const { data, error } = await supabase
          .schema('deepsearch')
          .from('behavioral_signals')
          .select('id')
          .eq('user_id', user.id)
          .eq('item_id', currentContent.id)
          .eq('interaction_type', 'like')
          .maybeSingle();

        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/d63f98f2-a476-45f2-8f18-652bfd36f226',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ContentDetailModal.jsx:253',message:'loadFavoriteStatus result',data:{hasError:!!error,errorCode:error?.code,hasData:!!data},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'B'})}).catch(()=>{});
        // #endregion
        // Silently handle all errors (403, etc.)
        if (error) {
          setIsFavorited(false);
        } else {
          setIsFavorited(!!data);
        }
      } catch (error) {
        // Silently ignore all errors
        setIsFavorited(false);
      }
    };

    if (isOpen && currentContent?.id && user?.id) {
      loadFavoriteStatus();
    }
  }, [currentContent?.id, user?.id, isOpen]);

  // Real-time subscription for content updates
  useEffect(() => {
    if (!isOpen || !content || !user?.id) return;

    const isDeepsearchItem = content._source === 'deepsearch.items' || 
                            content.source_type === 'image' || 
                            content.source_type === 'upload' ||
                            content.source_type === 'pdf' ||
                            content.source_type === 'video';

    const subscription = supabase
      .channel(`content-${content.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: isDeepsearchItem ? 'deepsearch' : 'public',
        table: isDeepsearchItem ? 'items' : 'instagram_content',
        filter: `id=eq.${content.id}`
      }, (payload) => {
        console.log('Real-time update in modal:', payload);
        if (isDeepsearchItem) {
          const updatedItem = payload.new;
          const mappedItem = {
            ...updatedItem,
            analysis_data: updatedItem.meta?.analysis_data || {
              summary: updatedItem.meta?.ai_analysis?.summary,
              ocr: {
                text: updatedItem.extracted_text,
                hasText: updatedItem.has_text,
                confidence: updatedItem.text_confidence,
              },
              colors: {
                primaryColor: updatedItem.primary_color,
                mood: updatedItem.color_mood,
                palette: updatedItem.color_palette,
              },
              tagging: {
                primaryTags: updatedItem.primary_tags,
                allTags: updatedItem.auto_tags,
              },
            },
            extracted_text: updatedItem.raw_text || updatedItem.extracted_text,
            auto_tags: updatedItem.auto_tags,
            primary_tags: updatedItem.primary_tags,
            primary_color: updatedItem.primary_color,
            color_mood: updatedItem.color_mood,
            color_palette: updatedItem.color_palette,
          };
          setCurrentContent(mappedItem);
        } else {
          setCurrentContent(payload.new);
        }
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [isOpen, content?.id, content?._source, content?.source_type, user?.id]);

  // Define loadRelatedItems before useEffects that use it
  const loadRelatedItems = useCallback(async (itemId) => {
    if (!itemId) {
      console.log('[RelatedItems] No itemId provided, skipping load');
      return;
    }
    
    console.log('[RelatedItems] Loading related items for:', itemId);
    // Clear old items and show loading IMMEDIATELY
    setRelatedItems([]);
    setLoadingRelatedItems(true);
    
    try {
      const result = await itemsService.getRelatedItems(itemId, 8);
      console.log('[RelatedItems] Received result:', { 
        itemId: result.itemId, 
        count: result.count, 
        relatedItemsCount: result.relatedItems?.length || 0,
        sample: result.relatedItems?.[0],
        allItems: result.relatedItems
      });
      
      // Ensure we have an array
      const rawItems = Array.isArray(result.relatedItems) ? result.relatedItems : [];
      
      // Deduplicate by id (keep first occurrence with highest similarity)
      const itemMap = new Map();
      const duplicateIds = [];
      
      for (const item of rawItems) {
        if (!item || !item.id) {
          console.warn('[RelatedItems] Filtered out invalid item:', item);
          continue;
        }
        if (item.id === itemId) {
          console.warn('[RelatedItems] Filtered out self-referencing item:', item.id);
          continue;
        }
        
        const existing = itemMap.get(item.id);
        if (existing) {
          // Keep the one with higher similarity
          if (item.similarity > existing.similarity) {
            itemMap.set(item.id, item);
            duplicateIds.push(item.id);
          } else {
            duplicateIds.push(item.id);
          }
        } else {
          itemMap.set(item.id, item);
        }
      }
      
      const uniqueItems = Array.from(itemMap.values());
      
      // Sort by similarity descending to maintain order
      uniqueItems.sort((a, b) => (b.similarity || 0) - (a.similarity || 0));
      
      console.log('[RelatedItems] After deduplication:', {
        rawCount: rawItems.length,
        uniqueCount: uniqueItems.length,
        duplicates: rawItems.length - uniqueItems.length,
        duplicateIds: duplicateIds.length > 0 ? duplicateIds : 'none',
        itemIds: uniqueItems.map(i => i.id)
      });
      
      setRelatedItems(uniqueItems);
      console.log('[RelatedItems] State updated, relatedItems count:', uniqueItems.length);
    } catch (error) {
      console.error('[RelatedItems] Failed to load related items:', error);
      console.error('[RelatedItems] Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      setRelatedItems([]);
    } finally {
      setLoadingRelatedItems(false);
    }
  }, []);

  // Load collections when modal opens
  useEffect(() => {
    if (isOpen && user?.id) {
      loadCollections();
    }
  }, [isOpen, user?.id]);

  // Load current collection when content changes
  useEffect(() => {
    if (isOpen && currentContent?.id) {
      loadCurrentCollection();
    } else if (!isOpen) {
      // Clear collection when modal closes
      setCurrentCollection(null);
    }
  }, [isOpen, currentContent?.id, currentContent?.topic_id, currentContent?.meta?.collection_id]);

  // Clear related items immediately when content changes (before loading new ones)
  useEffect(() => {
    if (currentContent?.id) {
      console.log('[RelatedItems] Content changed, clearing old items:', currentContent.id);
      setRelatedItems([]);
      setLoadingRelatedItems(true);
    }
  }, [currentContent?.id]);

  // Load related items when content changes or modal opens
  useEffect(() => {
    if (isOpen && currentContent?.id) {
      console.log('[RelatedItems] useEffect triggered', { 
        isOpen, 
        currentContentId: currentContent.id 
      });
      // Use a small delay to ensure state is cleared first
      const timeoutId = setTimeout(() => {
        loadRelatedItems(currentContent.id);
      }, 50);
      
      return () => {
        clearTimeout(timeoutId);
      };
    } else {
      console.log('[RelatedItems] useEffect skipped:', { 
        isOpen, 
        hasId: !!currentContent?.id,
        currentContentId: currentContent?.id 
      });
      // Clear items when modal closes
      if (!isOpen) {
        setRelatedItems([]);
        setLoadingRelatedItems(false);
      }
    }
  }, [currentContent?.id, isOpen, loadRelatedItems]);

  // Close delete menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDeleteMenu && !event.target.closest('.delete-menu-container')) {
        setShowDeleteMenu(false);
      }
    };
    if (showDeleteMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDeleteMenu]);

  const handleRelatedItemClick = async (relatedItem) => {
    if (!relatedItem?.id) {
      console.log('[RelatedItems] No item ID in handleRelatedItemClick');
      return;
    }
    
    console.log('[RelatedItems] Clicked related item:', relatedItem.id, relatedItem);
    
    // REMOVED: Auto-opening videos in new tab
    // Now all related items (including videos) open in the detail modal
    // Users can still click the external link button if they want to open the source
    
    console.log('[RelatedItems] Loading related item in drawer, id:', relatedItem.id);
    
    // CRITICAL FIX 1: Clear old related items IMMEDIATELY to prevent old cards from persisting
    setRelatedItems([]);
    setLoadingRelatedItems(true);
    
    // CRITICAL FIX 2: Set a loading placeholder immediately to prevent old thumbnail from flashing
    setCurrentContent({
      id: relatedItem.id,
      title: 'Loading...',
      source_type: relatedItem.sourceType,
      _loading: true, // Flag to indicate loading state
    });
    
    try {
      const isDeepsearchItem = relatedItem.sourceType === 'image' || 
                              relatedItem.sourceType === 'upload' ||
                              relatedItem.sourceType === 'pdf' ||
                              relatedItem.sourceType === 'video' ||
                              relatedItem.sourceType === 'audio' ||
                              relatedItem.sourceType === 'screenshot' ||
                              relatedItem.sourceType === 'site';
      
      if (isDeepsearchItem) {
        const { data: fullItem, error } = await supabase
          .schema('deepsearch')
          .from('items')
          .select('*, meta, custom_title')
          .eq('id', relatedItem.id)
          .single();
        
        if (!error && fullItem) {
          const meta = fullItem.meta || {};
          const channel = meta.channel || relatedItem.channel || null;
          const contentType = meta.content_type || null;
          const transcription = meta.transcription || null;
          
          // Determine actual source type: if transcription exists, it's a video
          let actualSourceType = fullItem.source_type;
          if (transcription && (transcription.full_transcript || transcription.text || transcription.transcript)) {
            actualSourceType = 'video';
          } else if (channel === 'YouTube' || channel === 'TikTok') {
            actualSourceType = 'video';
          } else if (channel === 'Instagram' && (contentType === 'reel' || contentType === 'video')) {
            actualSourceType = 'video';
          }
          
          const mappedItem = {
            ...fullItem,
            id: fullItem.id,
            title: fullItem.custom_title || fullItem.title || fullItem.description || 'Untitled',
            custom_title: fullItem.custom_title,
            description: fullItem.description,
            url: fullItem.url,
            image_url: fullItem.image_url,
            content_type: actualSourceType,
            source_type: actualSourceType,
            channel: channel,
            meta: meta, // Include meta for getThumbnailUrl
            transcription: transcription,
            analysis_data: meta.analysis_data || {
              summary: meta.ai_analysis?.summary,
              ocr: {
                text: fullItem.raw_text || fullItem.extracted_text,
                hasText: !!fullItem.raw_text,
                confidence: fullItem.text_confidence,
              },
              colors: {
                primaryColor: fullItem.primary_color,
                mood: fullItem.color_mood,
                palette: fullItem.color_palette,
              },
              tagging: {
                primaryTags: fullItem.primary_tags,
                allTags: fullItem.auto_tags,
              },
            },
            extracted_text: fullItem.raw_text || fullItem.extracted_text,
            auto_tags: fullItem.auto_tags,
            primary_tags: fullItem.primary_tags,
            primary_color: fullItem.primary_color,
            color_mood: fullItem.color_mood,
            color_palette: fullItem.color_palette,
            _source: 'deepsearch.items',
            embedding_text: fullItem.embedding_text,
            raw_text: fullItem.raw_text,
          };
          
          // Use getThumbnailUrl to get the best thumbnail (same as initial content loading)
          const rawThumbnailUrl = getThumbnailUrl(mappedItem);
          if (rawThumbnailUrl) {
            mappedItem.thumbnail_url = rawThumbnailUrl;
          }
          
          console.log('[RelatedItems] Setting new content:', {
            id: mappedItem.id,
            title: mappedItem.title,
            sourceType: actualSourceType,
            channel,
            url: mappedItem.url,
            image_url: mappedItem.image_url,
            rawThumbnailUrl,
            thumbnail_url: mappedItem.thumbnail_url,
            meta: mappedItem.meta,
            hasTranscription: !!transcription
          });
          // Update content - this will trigger the useEffect to load new related items
          setCurrentContent(mappedItem);
          // Keep the Related tab active when navigating to a related item
          setActiveTab('related');
          // Let the useEffect handle loading the related items
          return;
        }
      }
      
      const fallbackItem = {
        ...relatedItem,
        id: relatedItem.id,
        title: relatedItem.title || 'Untitled',
        description: relatedItem.description,
        url: relatedItem.url,
        source_type: relatedItem.sourceType,
        channel: relatedItem.channel,
        _source: 'deepsearch.items',
      };
      
      // Use getThumbnailUrl for consistent thumbnail resolution
      const rawThumbnailUrl = getThumbnailUrl(fallbackItem);
      if (rawThumbnailUrl) {
        fallbackItem.thumbnail_url = rawThumbnailUrl;
      }
      
      console.log('[RelatedItems] Setting fallback content with thumbnail:', rawThumbnailUrl);
      setCurrentContent(fallbackItem);
      // Keep the Related tab active when navigating to a related item
      setActiveTab('related');
    } catch (error) {
      console.error('[RelatedItems] Error loading related item:', error);
      setLoadingRelatedItems(false);
    }
  };

  const loadCollections = async () => {
    if (!user?.id) return;
    setLoadingCollections(true);
    try {
      await topicService.ensureAllCollection(user.id);
      const { data, error } = await topicService.getUserTopics(user.id);
      if (!error && data) {
        const sorted = [...(data || [])].sort((a, b) => {
          if (a.name === 'All') return -1;
          if (b.name === 'All') return 1;
          return a.name.localeCompare(b.name);
        });
        setCollections(sorted);
      }
    } catch (error) {
      console.error('Failed to load collections:', error);
    } finally {
      setLoadingCollections(false);
    }
  };

  const loadCurrentCollection = async () => {
    const collectionId = currentContent?.topic_id || currentContent?.meta?.collection_id;
    
    if (!collectionId) {
      setCurrentCollection(null);
      return;
    }
    
    try {
      const { data, error } = await topicService.getTopicWithContent(collectionId);
      if (!error && data) {
        setCurrentCollection(data);
      } else {
        setCurrentCollection(null);
      }
    } catch (error) {
      console.error('Failed to load current collection:', error);
      setCurrentCollection(null);
    }
  };

  const handleChangeCollection = async (collectionId) => {
    if (!currentContent?.id || !user?.id || changingCollection) {
      console.log('[CollectionChange] Skipping - missing data or already changing', {
        hasContentId: !!currentContent?.id,
        hasUserId: !!user?.id,
        changingCollection
      });
      return;
    }
    
    // If selecting empty value, clear the collection
    if (!collectionId || collectionId === '') {
      collectionId = null;
    }
    
    setChangingCollection(true);
    setCollectionChangeSuccess(false);
    
    console.log('[CollectionChange] Starting change', {
      itemId: currentContent.id,
      collectionId,
      currentCollection: currentCollection?.id
    });
    
    try {
      const isDeepsearchItem = currentContent?._source === 'deepsearch.items' || 
                               currentContent?.source_type === 'image' || 
                               currentContent?.source_type === 'upload' ||
                               currentContent?.source_type === 'pdf' ||
                               currentContent?.source_type === 'video' ||
                               currentContent?.source_type === 'audio' ||
                               currentContent?.source_type === 'screenshot' ||
                               currentContent?.source_type === 'site';
      
      if (isDeepsearchItem) {
        const { data: currentItem, error: fetchError } = await supabase
          .schema('deepsearch')
          .from('items')
          .select('meta')
          .eq('id', currentContent.id)
          .eq('user_id', user.id)
          .single();
        
        if (fetchError) {
          console.error('[CollectionChange] Failed to fetch current item:', fetchError);
          throw fetchError;
        }
        
        const currentMeta = currentItem?.meta || {};
        const updatedMeta = { ...currentMeta, collection_id: collectionId };
        
        const { error: updateError } = await supabase
          .schema('deepsearch')
          .from('items')
          .update({ meta: updatedMeta })
          .eq('id', currentContent.id)
          .eq('user_id', user.id);
        
        if (updateError) {
          console.error('[CollectionChange] Failed to update item:', updateError);
          throw updateError;
        }
        
        // Verify the update succeeded by fetching the item again
        const { data: verifyItem, error: verifyError } = await supabase
          .schema('deepsearch')
          .from('items')
          .select('meta')
          .eq('id', currentContent.id)
          .eq('user_id', user.id)
          .single();
        
        if (verifyError || verifyItem?.meta?.collection_id !== collectionId) {
          console.error('[CollectionChange] Verification failed:', verifyError);
          throw new Error('Collection update verification failed');
        }
        
        // Update state with verified data
        setCurrentContent(prev => ({ 
          ...prev, 
          meta: updatedMeta,
          topic_id: collectionId,
        }));
      } else {
        // For Instagram content
        const { error } = await supabase
          .schema('public')
          .from('instagram_content')
          .update({ topic_id: collectionId })
          .eq('id', currentContent.id)
          .eq('user_id', user.id);
        
        if (error) {
          console.error('[CollectionChange] Failed to update Instagram content:', error);
          throw error;
        }
        
        // Verify the update
        const { data: verifyItem, error: verifyError } = await supabase
          .schema('public')
          .from('instagram_content')
          .select('topic_id')
          .eq('id', currentContent.id)
          .eq('user_id', user.id)
          .single();
        
        if (verifyError || verifyItem?.topic_id !== collectionId) {
          console.error('[CollectionChange] Verification failed:', verifyError);
          throw new Error('Collection update verification failed');
        }
        
        setCurrentContent(prev => ({ ...prev, topic_id: collectionId }));
      }
      
      // Load the new collection using the collectionId directly (not from state)
      if (collectionId) {
        const { data: newCollection, error: collectionError } = await topicService.getTopicWithContent(collectionId);
        if (!collectionError && newCollection) {
          setCurrentCollection(newCollection);
          console.log('[CollectionChange] Loaded new collection:', newCollection.name);
        } else {
          setCurrentCollection(null);
          console.warn('[CollectionChange] Failed to load collection, but update succeeded');
        }
      } else {
        setCurrentCollection(null);
      }
      
      // Only show success checkmark after verification
      setCollectionChangeSuccess(true);
      console.log('[CollectionChange] Success - collection changed to:', collectionId);
      
      // Hide checkmark after 3 seconds
      setTimeout(() => {
        setCollectionChangeSuccess(false);
      }, 3000);
      
      window.dispatchEvent(new CustomEvent('contentCollectionChanged', {
        detail: { itemId: currentContent.id, collectionId },
      }));
    } catch (error) {
      console.error('[CollectionChange] Failed to change collection:', error);
      alert(`Failed to change collection: ${error.message || 'Please try again.'}`);
      setCollectionChangeSuccess(false);
      // Reload current collection to restore correct state
      await loadCurrentCollection();
    } finally {
      setChangingCollection(false);
    }
  };

  const handleStartEditTitle = () => {
    setEditedTitle(currentContent?.title || '');
    setIsEditingTitle(true);
  };

  const handleCancelEditTitle = () => {
    setIsEditingTitle(false);
    setEditedTitle('');
  };

  const handleSaveTitle = async () => {
    if (!currentContent?.id || !user?.id || isSavingTitle) return;
    
    const trimmedTitle = editedTitle.trim();
    if (!trimmedTitle) {
      // Don't allow empty titles
      handleCancelEditTitle();
      return;
    }
    
    setIsSavingTitle(true);
    
    try {
      const isDeepsearchItem = currentContent?._source === 'deepsearch.items' || 
                               currentContent?.source_type === 'image' || 
                               currentContent?.source_type === 'upload' ||
                               currentContent?.source_type === 'pdf' ||
                               currentContent?.source_type === 'video';
      
      if (isDeepsearchItem) {
        // Update custom_title in deepsearch.items
        const { error } = await supabase
          .schema('deepsearch')
          .from('items')
          .update({ custom_title: trimmedTitle })
          .eq('id', currentContent.id)
          .eq('user_id', user.id);
        
        if (error) throw error;
        
        // Update local state
        setCurrentContent(prev => ({
          ...prev,
          title: trimmedTitle,
          custom_title: trimmedTitle,
        }));
      } else {
        // For Instagram content, update title directly
        const { error } = await supabase
          .from('instagram_content')
          .update({ title: trimmedTitle })
          .eq('id', currentContent.id)
          .eq('user_id', user.id);
        
        if (error) throw error;
        
        setCurrentContent(prev => ({
          ...prev,
          title: trimmedTitle,
        }));
      }
      
      setIsEditingTitle(false);
      setEditedTitle('');
    } catch (error) {
      console.error('Failed to save title:', error);
      alert('Failed to save title. Please try again.');
    } finally {
      setIsSavingTitle(false);
    }
  };

  // Handle Ask Swirl with prompt
  const handleAskSwirlWithPrompt = () => {
    if (!currentContent?.id || !askSwirlPrompt.trim()) return;
    
    // Close the modal
    onClose();
    
    // Navigate to AI chat with the item and prompt
    const params = new URLSearchParams({
      content: currentContent.id,
      prompt: askSwirlPrompt.trim()
    });
    navigate(`/ai-chat?${params.toString()}`);
  };

  if (!isOpen || !currentContent) return null;

  // Get summary text - check multiple sources including book-specific fields
  const summaryText = (() => {
    // Early return if no currentContent
    if (!currentContent) return 'Summary not available';
    
    // Check all possible summary locations with safe navigation
    const summary = 
      currentContent.analysis_data?.summary || 
      currentContent.ai_analysis?.summary ||
      currentContent.meta?.analysis_data?.summary ||
      currentContent.meta?.ai_analysis?.summary ||
      // Book-specific fallbacks
      currentContent.meta?.bookDescription ||
      currentContent.description;

    if (typeof summary === 'string') {
      try {
        const parsed = JSON.parse(summary);
        return parsed.summary || parsed.text || summary;
      } catch {
        return summary;
      }
    } else if (typeof summary === 'object' && summary !== null) {
      return summary.summary || summary.text || summary.description || 'Summary not available';
    }
    return 'Summary not available';
  })();

  // Get key points - check multiple sources including book-specific fields
  const keyPoints =
    currentContent.analysis_data?.key_points ||
    currentContent.meta?.analysis_data?.key_points ||
    // Worker writes bullet points for videos here:
    currentContent.meta?.ai_analysis?.bullet_points ||
    currentContent.meta?.ai_analysis?.summary_points ||  // NEW: Instagram carousel points
    currentContent.meta?.ai_analysis?.action_points ||
    currentContent.meta?.ai_analysis?.key_points ||
    currentContent.key_points ||
    [];

  const normalizedKeyPoints = Array.isArray(keyPoints)
    ? keyPoints.filter(Boolean).map((p) => (typeof p === 'string' ? p : (p?.text || p?.title || JSON.stringify(p))))
    : [];

  // Get tags - check multiple sources including book categories
  const pickFirstNonEmptyArray = (...candidates) => {
    for (const c of candidates) {
      if (Array.isArray(c)) {
        const cleaned = c.filter(Boolean);
        if (cleaned.length > 0) return cleaned;
      }
    }
    return [];
  };

  const tags = pickFirstNonEmptyArray(
    currentContent.primary_tags,
    currentContent.auto_tags?.topics,
    currentContent.meta?.ai_analysis?.tags,
    currentContent.analysis_data?.tagging?.primaryTags,
    currentContent.meta?.analysis_data?.tagging?.primaryTags,
    currentContent.analysis_data?.tags,
    currentContent.tags,
  );

  const isVideoContent =
    currentContent.source_type === 'video' ||
    currentContent.content_type === 'video' ||
    currentContent.meta?.channel === 'YouTube';

  const fallbackTags = (() => {
    const m = currentContent.meta || {};
    const out = [];
    if (typeof m?.topic_name === 'string' && m.topic_name.trim()) out.push(m.topic_name.trim());
    if (typeof m?.topic === 'string' && m.topic.trim()) out.push(m.topic.trim());
    if (Array.isArray(m?.categories)) out.push(...m.categories.filter(Boolean));
    if (out.length > 0) return Array.from(new Set(out)).slice(0, 6);

    // Absolute last resort (no AI): derive a few tags from title/summary text
    const stop = new Set([
      'the','and','for','with','that','this','from','your','you','are','was','were','has','have','had','not','but',
      'they','their','them','his','her','she','him','its','our','out','into','about','over','under','more','most',
      'what','when','where','why','how','can','could','should','would','will','just','than','then','also',
      'video','youtube','instagram','tiktok','twitter','linkedin',
    ]);
    const src = `${currentContent.title || ''} ${summaryText || ''}`;
    const tokens = src
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, ' ')
      .split(/\s+/)
      .map((t) => t.trim())
      .filter((t) => t.length >= 4 && !stop.has(t));
    const counts = new Map();
    for (const t of tokens) counts.set(t, (counts.get(t) || 0) + 1);
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([t]) => t.replace(/^\w/, (c) => c.toUpperCase()));
  })();

  const resolvedTags = (tags && tags.length > 0) ? tags : fallbackTags;

  const resolvedConfidence = (() => {
    const v =
      currentContent.analysis_data?.confidence_score ??
      currentContent.confidence_score ??
      currentContent.meta?.ai_analysis?.confidence_score ??
      currentContent.meta?.ai_analysis?.confidence ??
      null;
    if (typeof v === 'number' && Number.isFinite(v)) return Math.max(0, Math.min(1, v));

    // If we have transcript text already, show a conservative confidence so UI stays consistent.
    if (isVideoContent) {
      const raw = (currentContent.raw_text || '').trim();
      const approx = raw.length >= 2000 ? 0.85 : raw.length >= 600 ? 0.7 : null;
      if (approx) return approx;
    }
    return null;
  })();

  // Get visual elements - check multiple sources
  const visualElements = 
    currentContent.analysis_data?.visual_elements ||
    currentContent.meta?.analysis_data?.visual_elements ||
    currentContent.meta?.ai_analysis?.visual_elements ||
    [];

  // Get detailed analysis
  const detailedAnalysis = 
    currentContent.analysis_data?.detailed_analysis ||
    currentContent.meta?.analysis_data?.detailed_analysis ||
    currentContent.meta?.ai_analysis?.detailed_analysis ||
    null;

  // Get book-specific metadata
  const isBook = currentContent.source_type === 'book';
  const bookAuthor = currentContent.meta?.author || null;
  const bookPublisher = currentContent.meta?.publisher || null;
  const bookPageCount = currentContent.meta?.pageCount || null;
  const bookRating = currentContent.meta?.averageRating || null;

  // Get product-specific metadata
  const isProduct = currentContent.source_type === 'product';
  const productBrand = currentContent.meta?.brand || null;
  const productCategory = currentContent.meta?.category || null;
  const productAsin = currentContent.meta?.asin || null;

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get content type label (capitalized)
  const contentTypeLabel = (() => {
    const type = currentContent.source_type || currentContent.content_type || 'content';
    return type.charAt(0).toUpperCase() + type.slice(1);
  })();

  // Check if has image or video
  const mediaCandidateUrl =
    getThumbnailUrl(currentContent) ||
    currentContent.thumbnail_url ||
    currentContent.image_url ||
    currentContent.url ||
    null;

  const isImageLikeUrl = (u) =>
    typeof u === 'string' && /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(u);

  const hasImage =
    !!mediaCandidateUrl &&
    (currentContent.source_type === 'image' ||
      currentContent.content_type === 'image' ||
      currentContent.source_type === 'carousel' ||  // Carousel is also an image type
      // Books and products should show their cover/product image if present
      currentContent.source_type === 'book' ||
      currentContent.source_type === 'product' ||
      isImageLikeUrl(mediaCandidateUrl));
  
  const hasVideo = (currentContent.url || currentContent.thumbnail_url || currentContent.image_url) && 
                   (currentContent.source_type === 'video' || 
                    currentContent.content_type === 'video' || 
                    currentContent.url?.match(/\.(mp4|webm|mov|quicktime)$/i));
  
  const hasMedia = hasImage || hasVideo;
  
  // Carousel detection - check meta.carousel_images for multiple images
  const carouselImages = currentContent?.meta?.carousel_images || [];
  const isCarousel = carouselImages.length > 1 || currentContent?.source_type === 'carousel' || currentContent?.meta?.instagram_type === 'carousel';
  const totalCarouselImages = carouselImages.length > 0 ? carouselImages.length : 1;
  
  // Carousel navigation handlers
  const goToPrevImage = (e) => {
    e.stopPropagation();
    setCarouselIndex((prev) => (prev > 0 ? prev - 1 : totalCarouselImages - 1));
  };
  const goToNextImage = (e) => {
    e.stopPropagation();
    setCarouselIndex((prev) => (prev < totalCarouselImages - 1 ? prev + 1 : 0));
  };
  
  // Get current carousel image URL
  const getCurrentCarouselImage = () => {
    if (carouselImages.length > 0 && carouselIndex < carouselImages.length) {
      return carouselImages[carouselIndex];
    }
    // Fallback to regular thumbnail
    return getThumbnailUrl(currentContent) || currentContent?.thumbnail_url || currentContent?.image_url;
  };
  const isLoading = currentContent?._loading === true;
  
  // Debug logging for media detection
  if (currentContent?.id && !isLoading) {
    console.log('[Modal] Media detection:', {
      id: currentContent.id,
      title: currentContent.title,
      hasImage,
      hasVideo,
      hasMedia,
      source_type: currentContent.source_type,
      content_type: currentContent.content_type,
      url: currentContent.url,
      thumbnail_url: currentContent.thumbnail_url,
      image_url: currentContent.image_url,
    });
  }
  
  // Check if has transcript
  const transcriptText =
    currentContent.transcription?.full_transcript ||
    currentContent.transcription?.text ||
    currentContent.transcription?.transcript ||
    (typeof currentContent.transcription === 'string' ? currentContent.transcription : null) ||
    // Worker writes transcript to deepsearch.items.raw_text:
    currentContent.raw_text ||
    currentContent.extracted_text ||
    currentContent.meta?.ai_analysis?.transcript ||
    null;

  const hasTranscript = typeof transcriptText === 'string' && transcriptText.trim().length > 0;

  return (
    <>
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" 
      style={{ fontFamily: '"Inter", sans-serif' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-2xl shadow-lg w-[1100px] h-[85vh] overflow-hidden relative flex flex-row" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        
        {/* Loading indicator when switching between items */}
        {isLoading && (
          <div className="w-full h-[200px] bg-gray-100 flex items-center justify-center" style={{ borderRadius: '16px 16px 0 0' }}>
            <div className="text-gray-500">Loading...</div>
          </div>
        )}
        
        {/* Left Side: Media Section (Image/Video) */}
        {hasMedia && !isLoading && (
          <div className="w-[500px] h-full flex-shrink-0 relative bg-gray-100">
            {hasVideo ? (
              <div 
                className="w-full h-full bg-black flex items-center justify-center relative cursor-pointer group" 
                style={{ borderRadius: '16px 0 0 16px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (currentContent.url) {
                    window.open(currentContent.url, '_blank', 'noopener,noreferrer');
                  }
                }}
              >
                {(() => {
                  // CRITICAL DEBUG: Only log when opening video
                  if (hasVideo) {
                    console.log('🎬 [VIDEO IN MODAL] FULL DATA:');
                    console.log('  - id:', currentContent.id);
                    console.log('  - title:', currentContent.title?.substring(0, 50));
                    console.log('  - source_type:', currentContent.source_type);
                    console.log('  - channel:', currentContent.channel);
                    console.log('  - thumbnail_url:', currentContent.thumbnail_url);
                    console.log('  - image_url:', currentContent.image_url);
                    console.log('  - url:', currentContent.url);
                    console.log('  - meta?.thumbnail_url:', currentContent.meta?.thumbnail_url);
                    console.log('  - meta?.thumbnail:', currentContent.meta?.thumbnail);
                    console.log('  - meta?.transcription:', currentContent.meta?.transcription);
                    console.log('  - ALL meta keys:', currentContent.meta ? Object.keys(currentContent.meta) : 'NO META');
                  }
                  
                  // Use same thumbnail resolution as Swirlspace ContentCard
                  const rawThumbnailUrl = getThumbnailUrl(currentContent) || 
                                         currentContent.thumbnail_url || 
                                         currentContent.image_url || 
                                         currentContent.meta?.thumbnail_url;
                  
                  const thumbnailUrl = rawThumbnailUrl ? getImageUrl({ image: rawThumbnailUrl, thumbnail_url: rawThumbnailUrl }) : null;
                  
                  if (hasVideo) {
                    console.log('🎬 [VIDEO IN MODAL] Resolved thumbnail:', {
                      rawThumbnailUrl,
                      thumbnailUrl
                    });
                  }
                  
                  return thumbnailUrl ? (
                    <img
                      src={thumbnailUrl}
                      alt={currentContent.title || 'Video'}
                      className="w-full h-full object-cover"
                      style={{ borderRadius: '16px 0 0 16px' }}
                      onError={(e) => {
                        // Hide broken image, show play button on black background
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : null;
                })()}
                {/* Play button overlay - always visible for videos */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors pointer-events-none">
                  <div className="w-16 h-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform pointer-events-auto cursor-pointer">
                    <span className="text-gray-900 text-2xl ml-1">▶</span>
                  </div>
                </div>
                {/* Video badge */}
                {currentContent.url && (
                  <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-sm rounded px-2 py-1 pointer-events-none">
                    <span className="text-white text-xs font-medium">Video</span>
                  </div>
                )}
              </div>
            ) : (
              /* Outer wrapper for image + arrows - arrows are OUTSIDE group to prevent hover conflicts */
              <div className="relative w-full h-full">
                {/* Inner group container for image + magnifying glass hover effect */}
                <div
                  className="relative cursor-pointer group w-full h-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Books: clicking the cover should open the original URL
                    if (currentContent.source_type === 'book' && currentContent.url) {
                      window.open(currentContent.url, '_blank', 'noopener,noreferrer');
                      return;
                    }

                    // For carousels, use current carousel image
                    const rawThumbnailUrl = isCarousel 
                      ? getCurrentCarouselImage()
                      : (getThumbnailUrl(currentContent) || currentContent.thumbnail_url || currentContent.image_url || null);
                    const imageUrl = rawThumbnailUrl
                      ? getImageUrl({ image: rawThumbnailUrl, thumbnail_url: rawThumbnailUrl })
                      : null;
                    if (imageUrl) setFullScreenImage(imageUrl);
                  }}
                >
                  {(() => {
                    // For carousels, use current carousel image based on index
                    const rawThumbnailUrl = isCarousel 
                      ? getCurrentCarouselImage()
                      : (getThumbnailUrl(currentContent) || currentContent.thumbnail_url || currentContent.image_url || null);
                    const imageUrl = rawThumbnailUrl
                      ? getImageUrl({ image: rawThumbnailUrl, thumbnail_url: rawThumbnailUrl })
                      : null;

                    return imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={currentContent.title || 'Content'}
                        className="w-full h-full object-cover"
                        style={{ borderRadius: '16px 0 0 16px' }}
                        loading="eager"
                        fetchpriority="high"
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                          // If something goes wrong, hide the broken image rather than showing a placeholder
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : null;
                  })()}
                  
                  {/* Magnifying glass overlay - with hover effect */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-all duration-200 pointer-events-none z-10" style={{ borderRadius: '16px 0 0 16px' }}>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                        <ZoomIn size={24} className="text-gray-700" />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Carousel Navigation - OUTSIDE group container, NO transitions */}
                {isCarousel && totalCarouselImages > 1 && (
                  <>
                    {/* Left Arrow - completely static, no animations */}
                    <button
                      onClick={(e) => { e.stopPropagation(); goToPrevImage(e); }}
                      className="absolute left-2 top-1/2 -translate-y-1/2 text-white rounded-full p-2.5 z-50"
                      style={{ 
                        backgroundColor: '#333333',
                        transition: 'none',
                        transform: 'translateY(-50%)',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
                      }}
                      aria-label="Previous image"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    {/* Right Arrow - completely static, no animations */}
                    <button
                      onClick={(e) => { e.stopPropagation(); goToNextImage(e); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-white rounded-full p-2.5 z-50"
                      style={{ 
                        backgroundColor: '#333333',
                        transition: 'none',
                        transform: 'translateY(-50%)',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
                      }}
                      aria-label="Next image"
                    >
                      <ChevronRight size={20} />
                    </button>
                    {/* Carousel Dots Indicator */}
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-50">
                      {carouselImages.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCarouselIndex(idx);
                          }}
                          className={`w-2 h-2 rounded-full transition-all ${
                            idx === carouselIndex 
                              ? 'bg-white scale-110' 
                              : 'bg-white/50 hover:bg-white/70'
                          }`}
                          aria-label={`Go to image ${idx + 1}`}
                        />
                      ))}
                    </div>
                    {/* Image Counter Badge */}
                    <div className="absolute top-2 left-2 rounded px-2 py-1 z-50" style={{ backgroundColor: '#222222' }}>
                      <span className="text-white text-xs font-medium">{carouselIndex + 1} / {totalCarouselImages}</span>
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="absolute top-3 left-3 flex flex-col gap-2.5 z-10">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className="bg-white/80 border-none rounded-full w-8 h-8 flex items-center justify-center cursor-pointer hover:bg-white/90 transition-colors"
                title="Close"
              >
                <X size={16} className="text-gray-700" />
              </button>
              <button
                className={`bg-white/80 border-none rounded-full w-8 h-8 flex items-center justify-center cursor-pointer hover:bg-white/90 transition-colors ${isFavorited ? 'text-red-500' : 'text-gray-700'}`}
                title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                onClick={async (e) => {
                  e.stopPropagation();
                  const newFavoriteStatus = !isFavorited;
                  setIsFavorited(newFavoriteStatus); // Optimistic update
                  
                  try {
                    if (currentContent?.id && user?.id) {
                      if (newFavoriteStatus) {
                        // Add favorite
                        const { error } = await supabase
                          .schema('deepsearch')
                          .from('behavioral_signals')
                          .insert({
                            user_id: user.id,
                            item_id: currentContent.id,
                            interaction_type: 'like',
                          });

                        if (error) throw error;
                      } else {
                        // Remove favorite
                        const { error } = await supabase
                          .schema('deepsearch')
                          .from('behavioral_signals')
                          .delete()
                          .eq('user_id', user.id)
                          .eq('item_id', currentContent.id)
                          .eq('interaction_type', 'like');

                        if (error) throw error;
                      }
                      
                      // Dispatch event to refresh lists
                      window.dispatchEvent(new CustomEvent('favoriteChanged', {
                        detail: { itemId: currentContent.id, isFavorited: newFavoriteStatus },
                      }));
                    }
                  } catch (error) {
                    console.error('Failed to update favorite status:', error);
                    // Revert on error
                    setIsFavorited(!newFavoriteStatus);
                    alert('Failed to update favorite status. Please try again.');
                  }
                }}
              >
                <Heart size={16} className={isFavorited ? 'fill-current' : ''} />
              </button>
              <div className="relative delete-menu-container">
                <button
                  className="bg-white/80 border-none rounded-full w-8 h-8 flex items-center justify-center cursor-pointer hover:bg-white/90 transition-colors"
                  title="More"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteMenu(!showDeleteMenu);
                  }}
                >
                  <MoreVertical size={16} className="text-gray-700" />
                </button>
                {showDeleteMenu && (
                  <div className="absolute top-10 left-0 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20 min-w-[140px]">
                    {/* Reprocess Option */}
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        setShowDeleteMenu(false);
                        
                        try {
                          const isImage = currentContent.source_type === 'image' || 
                                         (currentContent.image_url && !currentContent.url?.includes('youtube.com') && !currentContent.url?.includes('instagram.com') && !currentContent.url?.includes('tiktok.com'));
                          
                          if (isImage) {
                            // ROUTE 1: Images -> Queue to deepsearch.ingest_queue for worker to process with OCR
                            console.log('🖼️ [Reprocess] Image detected - queuing for deepsearch-worker');
                            
                            // Use RPC to queue the reprocess job (bypasses RLS issues)
                            // NOTE: Database trigger will automatically call swirl-ingestion-worker
                            // But we need to check the provider field to route correctly
                            const { data: result, error: jobError } = await supabase
                              .rpc('queue_reprocess_job', {
                                p_item_id: currentContent.id,
                                p_provider: 'image'  // Set provider to 'image' so worker knows to handle it
                              });

                            if (jobError || !result?.ok) {
                              throw new Error(`Failed to queue reprocess job: ${jobError?.message || result?.error}`);
                            }

                            console.log(`✅ [Reprocess] Job queued:`, result);
                            
                            // Add to processing queue UI to show toast notification
                            processingQueue.addToQueue({
                              id: currentContent.id,
                              title: currentContent.title || 'Image',
                              source_type: 'image',
                              image_url: currentContent.image_url,
                              channel: 'DeepSearch'
                            }, {
                              silent: false
                            });
                            
                            // Database trigger will automatically call the worker - no need to manually trigger
                          } else {
                            // ROUTE 2: Videos -> Queue to deepsearch.ingest_queue with gettranscribe provider
                            console.log('🎬 [Reprocess] Video detected - queuing for swirl-ingestion-worker');
                            
                            // Use RPC to queue the reprocess job (bypasses RLS issues)
                            const { data: result, error: jobError} = await supabase
                              .rpc('queue_reprocess_job', {
                                p_item_id: currentContent.id,
                                p_provider: 'gettranscribe'  // Videos use gettranscribe provider
                              });

                            if (jobError || !result?.ok) {
                              throw new Error(`Failed to queue reprocess job: ${jobError?.message || result?.error}`);
                            }

                            console.log(`✅ [Reprocess] Video job queued:`, result);
                            
                            // Trigger the worker immediately
                            try {
                              await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/swirl-ingestion-worker`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'x-worker-secret': 'swirl-worker-secret-2025'
                                },
                                body: JSON.stringify({})
                              });
                              console.log('✅ [Reprocess] Worker triggered');
                            } catch (workerError) {
                              console.warn('⚠️ [Reprocess] Worker trigger failed (non-fatal):', workerError);
                            }
                          }
                          
                          // Dispatch event to refresh the UI
                          window.dispatchEvent(new CustomEvent('contentUpdated', { detail: { itemId: currentContent.id } }));
                          
                          // Close modal immediately - processing happens in background
                          onClose();
                        } catch (error) {
                          console.error('Failed to reprocess item:', error);
                          alert(`Failed to reprocess: ${error.message}`);
                          // Close modal even if there's an error
                          onClose();
                        }
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 transition-colors flex items-center gap-2"
                    >
                      <RefreshCw size={14} />
                      Reprocess
                    </button>
                    
                    {/* Delete Option */}
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (window.confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
                          try {
                            const isDeepsearchItem = currentContent?._source === 'deepsearch.items' || 
                                                     currentContent?.source_type === 'image' || 
                                                     currentContent?.source_type === 'upload' ||
                                                     currentContent?.source_type === 'video';
                            
                            if (isDeepsearchItem && currentContent?.id && user?.id) {
                              const { error } = await supabase
                                .schema('deepsearch')
                                .from('items')
                                .delete()
                                .eq('id', currentContent.id)
                                .eq('user_id', user.id);
                              
                              if (error) throw error;
                              
                              // Close modal and refresh
                              onClose();
                              window.dispatchEvent(new CustomEvent('contentDeleted', { detail: { itemId: currentContent.id } }));
                            } else if (currentContent?.id && user?.id) {
                              const { error } = await supabase
                                .schema('public')
                                .from('instagram_content')
                                .delete()
                                .eq('id', currentContent.id)
                                .eq('user_id', user.id);
                              
                              if (error) throw error;
                              
                              onClose();
                              window.dispatchEvent(new CustomEvent('contentDeleted', { detail: { itemId: currentContent.id } }));
                            }
                          } catch (error) {
                            console.error('Failed to delete item:', error);
                            alert('Failed to delete item. Please try again.');
                          }
                        }
                        setShowDeleteMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      Delete
          </button>
        </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Close button for non-media items - always visible in header area */}
        {!hasMedia && (
          <div className="absolute top-3 right-3 z-50">
              <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="bg-white/90 border border-gray-200 rounded-full w-8 h-8 flex items-center justify-center cursor-pointer hover:bg-white transition-colors shadow-sm"
              title="Close"
            >
              <X size={16} className="text-gray-700" />
              </button>
          </div>
        )}

        {/* Right Side: Content Section */}
        <div className="flex-1 flex flex-col min-w-0 overflow-y-auto">

        {/* Card Content */}
        <div className="px-4 pt-4 pb-2 flex-1 flex flex-col min-h-0">
          {/* Title with Edit Button */}
          <div 
            className="group relative flex items-center gap-2 mb-0.5"
            style={{ fontSize: '1.15rem', fontWeight: 600 }}
          >
            {isEditingTitle ? (
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveTitle();
                    } else if (e.key === 'Escape') {
                      handleCancelEditTitle();
                    }
                  }}
                  className="flex-1 px-2 py-1 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  style={{ fontSize: '1.15rem', fontWeight: 600 }}
                  autoFocus
                  disabled={isSavingTitle}
                />
                <button
                  onClick={handleSaveTitle}
                  disabled={isSavingTitle}
                  className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                  title="Save"
                >
                  <Check size={18} />
                </button>
                <button
                  onClick={handleCancelEditTitle}
                  disabled={isSavingTitle}
                  className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors"
                  title="Cancel"
                >
                  <XIcon size={18} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <div className="text-lg font-semibold" style={{ fontSize: '1.15rem', fontWeight: 600 }}>
                  {decodeHtmlEntities(currentContent.title) || 'Untitled'}
                </div>
                <button
                  onClick={handleStartEditTitle}
                  className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-all duration-200 flex-shrink-0"
                  title="Edit title"
                >
                  <Pencil size={16} />
                </button>
              </div>
            )}
          </div>

          {/* Meta */}
          <div className="text-gray-500 mb-2" style={{ color: '#777', fontSize: '0.85rem' }}>
            {contentTypeLabel} · {formatDate(currentContent.created_at)}
          </div>

          {/* Summary Box */}
          {summaryText && summaryText !== 'Summary not available' && (
            <div className="bg-gray-100 rounded-xl p-2.5 mb-1.5" style={{ background: '#f4f4f6', borderRadius: '12px', padding: '10px', fontSize: '0.875rem', lineHeight: '1.5' }}>
              <p className="text-gray-700 leading-relaxed">{decodeHtmlEntities(summaryText)}</p>
            </div>
          )}

          {/* Tabs */}
          <div className="flex border-b border-gray-200 mt-1">
            <button
              onClick={() => setActiveTab('summary')}
              className={`flex-1 text-center py-3 font-medium transition-all duration-200 ${
                activeTab === 'summary'
                  ? 'text-purple-700 border-b-2 border-purple-700'
                  : 'text-gray-600'
              }`}
              style={{ color: activeTab === 'summary' ? '#5b21b6' : '#666', fontWeight: 500 }}
            >
              Summary
            </button>
            <button
              onClick={() => setActiveTab('details')}
              className={`flex-1 text-center py-3 font-medium transition-all duration-200 ${
                activeTab === 'details'
                  ? 'text-purple-700 border-b-2 border-purple-700'
                  : 'text-gray-600'
              }`}
              style={{ color: activeTab === 'details' ? '#5b21b6' : '#666', fontWeight: 500 }}
            >
              Details
            </button>
            {isVideoContent && hasTranscript && (
              <button
                onClick={() => setActiveTab('transcript')}
                className={`flex-1 text-center py-3 font-medium transition-all duration-200 ${
                  activeTab === 'transcript'
                    ? 'text-purple-700 border-b-2 border-purple-700'
                    : 'text-gray-600'
                }`}
                style={{ color: activeTab === 'transcript' ? '#5b21b6' : '#666', fontWeight: 500 }}
              >
                Transcript
              </button>
            )}
            <button
              onClick={() => setActiveTab('related')}
              className={`flex-1 text-center py-3 font-medium transition-all duration-200 ${
                activeTab === 'related'
                  ? 'text-purple-700 border-b-2 border-purple-700'
                  : 'text-gray-600'
              }`}
              style={{ color: activeTab === 'related' ? '#5b21b6' : '#666', fontWeight: 500 }}
            >
              Related
            </button>
        </div>

          {/* Tab Content */}
          <div className="overflow-y-auto flex-1 min-h-0 mt-0.5" style={{ maxHeight: 'calc(90vh - 380px)', scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}>
            {/* Summary Tab - Key Points, Tags, Color Analysis, etc. */}
            {activeTab === 'summary' && (
              <div className="py-3 px-4 space-y-4" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                
                {/* Book Info - Special section for books */}
                {isBook && (bookAuthor || bookPublisher || bookPageCount || bookRating) && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Book Info</h3>
                    <div className="bg-white rounded-xl p-4 border border-gray-200 space-y-2">
                      {bookAuthor && (
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-700">Author:</span>
                          <span className="text-gray-600">{bookAuthor}</span>
                        </div>
                      )}
                      {bookPublisher && (
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-700">Publisher:</span>
                          <span className="text-gray-600">{bookPublisher}</span>
                        </div>
                      )}
                      {bookPageCount && (
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-700">Pages:</span>
                          <span className="text-gray-600">{bookPageCount}</span>
                        </div>
                      )}
                      {bookRating && (
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-700">Rating:</span>
                          <span className="text-gray-600">⭐ {bookRating}/5</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Product Info - Special section for products */}
                {isProduct && (productBrand || productCategory || productAsin) && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Info</h3>
                    <div className="bg-white rounded-xl p-4 border border-gray-200 space-y-2">
                      {productBrand && (
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-700">Brand:</span>
                          <span className="text-gray-600">{productBrand}</span>
                        </div>
                      )}
                      {productCategory && (
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-700">Category:</span>
                          <span className="text-gray-600">{productCategory}</span>
                        </div>
                      )}
                      {productAsin && (
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-700">ASIN:</span>
                          <span className="text-gray-600 font-mono text-sm">{productAsin}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Key Points / Bullet Points */}
              {normalizedKeyPoints.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {currentContent.source_type === 'video' ? 'Bullet Points' : 'Key Points'}
                  </h3>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <ul className="space-y-3">
                      {normalizedKeyPoints.map((point, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-medium">
                            {index + 1}
                          </span>
                          <span className="text-gray-700">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

                {/* Tags */}
                {resolvedTags.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                      {resolvedTags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full flex items-center space-x-1"
                          style={{ background: '#ede9fe', color: '#5b21b6', borderRadius: '8px', padding: '4px 10px', fontSize: '0.8rem' }}
                        >
                          <Tag size={12} />
                          <span>{typeof tag === 'string' ? tag : tag.name || tag.label || JSON.stringify(tag)}</span>
                        </span>
                      ))}
                  </div>
                </div>
              )}

                {/* Visual Elements */}
                {visualElements?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Visual Elements</h3>
                    <div className="flex flex-wrap gap-2">
                      {visualElements.map((element, index) => (
                        <span
                          key={index}
                          className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm rounded-lg capitalize"
                        >
                          {element}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Color Analysis */}
              {(currentContent.primary_color || currentContent.color_mood || currentContent.analysis_data?.colors?.primaryColor || currentContent.auto_tags?.colors?.length > 0) && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Color Analysis</h3>
                  <div className="bg-white rounded-xl p-4 border border-gray-200 space-y-3">
                    {currentContent.primary_color && (
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-12 h-12 rounded-lg border-2 border-gray-200"
                          style={{ backgroundColor: currentContent.primary_color }}
                        />
                        <div>
                          <p className="font-medium text-gray-900">Primary Color</p>
                          <p className="text-sm text-gray-600">{currentContent.primary_color}</p>
                        </div>
                      </div>
                    )}
                    {currentContent.color_mood && (
                      <div>
                        <p className="font-medium text-gray-900">Color Mood</p>
                        <p className="text-sm text-gray-600 capitalize">{currentContent.color_mood}</p>
                      </div>
                    )}
                    {currentContent.auto_tags?.colors && currentContent.auto_tags.colors.length > 0 && (
                      <div>
                        <p className="font-medium text-gray-900 mb-2">Detected Colors</p>
                        <div className="flex flex-wrap gap-2">
                          {currentContent.auto_tags.colors.map((color, index) => (
                            <span
                              key={index}
                              className="px-3 py-1.5 bg-gray-100 text-gray-800 text-sm rounded-lg capitalize font-medium"
                            >
                              {color}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {currentContent.color_palette && (
                      <div>
                        <p className="font-medium text-gray-900 mb-2">Color Palette</p>
                        <div className="flex flex-wrap gap-2">
                            {(currentContent.color_palette.colors || currentContent.color_palette || []).slice(0, 8).map((color, index) => (
                            <div 
                              key={index}
                              className="w-8 h-8 rounded border border-gray-200"
                              style={{ backgroundColor: color }}
                              title={color}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

                {/* Confidence Score */}
              {(currentContent.analysis_data?.confidence_score ||
                currentContent.confidence_score ||
                currentContent.meta?.ai_analysis?.confidence_score ||
                currentContent.meta?.ai_analysis?.confidence) && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Confidence Score</h3>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                          style={{ 
                              width: `${(((currentContent.analysis_data?.confidence_score ||
                                currentContent.confidence_score ||
                                currentContent.meta?.ai_analysis?.confidence_score ||
                                currentContent.meta?.ai_analysis?.confidence) || 0) * 100)}%` 
                          }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {(((currentContent.analysis_data?.confidence_score ||
                          currentContent.confidence_score ||
                          currentContent.meta?.ai_analysis?.confidence_score ||
                          currentContent.meta?.ai_analysis?.confidence) || 0) * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
                {/* Main Topics */}
                {(currentContent.analysis_data?.main_topics || currentContent.main_topics)?.length > 0 && (
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Main Topics</h3>
                    <div className="flex flex-wrap gap-2">
                      {(currentContent.analysis_data?.main_topics || currentContent.main_topics).map((topic, index) => (
                        <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                          {topic}
                        </span>
                    ))}
                  </div>
                </div>
              )}
              
                {/* Visual Labels */}
                {currentContent.visual_labels && currentContent.visual_labels.length > 0 && (
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Visual Elements</h3>
                  <div className="flex flex-wrap gap-2">
                      {currentContent.visual_labels.map((label, index) => (
                        <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                          {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

            {/* Details Tab - Combined Details and Metadata */}
            {activeTab === 'details' && (
              <div className="py-3 px-4 space-y-4" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                {/* OCR Text (non-video only). Videos show transcript in the Transcript tab. */}
                {!isVideoContent && (currentContent.extracted_text || currentContent.analysis_data?.ocr?.text || currentContent.raw_text) && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Extracted Text
                    </h3>
                    {(currentContent.text_confidence || currentContent.analysis_data?.ocr?.confidence) && 
                     ((currentContent.text_confidence || currentContent.analysis_data?.ocr?.confidence) < 0.7) && (
                      <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-yellow-800">Low OCR Confidence</p>
                            <p className="text-xs text-yellow-700 mt-1">
                              OCR accuracy is low ({((currentContent.text_confidence || currentContent.analysis_data?.ocr?.confidence) * 100).toFixed(1)}%). 
                              The extracted text below may contain errors.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="bg-white rounded-xl p-4 border border-gray-200">
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {currentContent.extracted_text || currentContent.analysis_data?.ocr?.text || currentContent.raw_text}
                      </p>
                      {(currentContent.text_confidence || currentContent.analysis_data?.ocr?.confidence) && (
                        <p className="text-xs text-gray-500 mt-2">
                          Confidence: {((currentContent.text_confidence || currentContent.analysis_data?.ocr?.confidence) * 100).toFixed(1)}%
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Full Description */}
                {currentContent.description && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Description</h3>
                    <div className="bg-white rounded-xl p-4 border border-gray-200">
                      <p className="text-gray-700">{currentContent.description}</p>
                    </div>
                  </div>
                )}

                {/* Metadata Section */}
              <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Metadata</h3>
                  <div className="bg-white rounded-xl p-4 border border-gray-200 space-y-2">
                    {currentContent.url && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1"><strong>Source URL:</strong></p>
                        <a href={currentContent.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                          {currentContent.url}
                        </a>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-600 mb-1"><strong>Uploaded by:</strong></p>
                      <p className="text-gray-900">You</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1"><strong>File Type:</strong></p>
                      <p className="text-gray-900">{contentTypeLabel}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1"><strong>Created at:</strong></p>
                      <p className="text-gray-900">{formatDate(currentContent.created_at)}</p>
                    </div>
                    {currentContent.channel && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1"><strong>Channel:</strong></p>
                        <p className="text-gray-900">{currentContent.channel}</p>
                      </div>
                    )}
                    {currentContent.source_type && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1"><strong>Source Type:</strong></p>
                        <p className="text-gray-900">{currentContent.source_type}</p>
                      </div>
                    )}
                  </div>
                </div>

                {!currentContent.extracted_text && !currentContent.analysis_data?.ocr?.text && !currentContent.raw_text && !currentContent.description && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No additional details available for this content.</p>
                  </div>
                )}
              </div>
            )}

            {/* Transcript Tab */}
            {activeTab === 'transcript' && hasTranscript && (
              <div className="py-3 px-4" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Full Transcript</h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(transcriptText || '');
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors text-sm"
                    >
                      <Copy size={14} />
                      <span>{copied ? 'Copied!' : 'Copy'}</span>
                    </button>
                    <button
                      onClick={() => {
                        const blob = new Blob([transcriptText || ''], { type: 'text/plain' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${currentContent.title}_transcript.txt`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors text-sm"
                    >
                      <Download size={14} />
                      <span>Download</span>
                    </button>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="prose prose-sm max-w-none">
                    <p className="text-gray-800 whitespace-pre-line leading-relaxed">
                      {transcriptText || ''}
                        </p>
                      </div>
                    </div>
                  </div>
            )}


            {/* Related Items Tab */}
            {activeTab === 'related' && (
              <div className="py-3 px-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Related</h3>
                <RelatedItemsRow
                  items={relatedItems}
                  loading={loadingRelatedItems}
                  onItemClick={handleRelatedItemClick}
                />
                {!loadingRelatedItems && relatedItems.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No related items found.</p>
                  </div>
                )}
              </div>
            )}

              </div>
          </div>

        {/* Consolidated Action Bar - Compact for side layout */}
        <div className="border-t border-gray-100 px-2 py-2 bg-gray-50/60 backdrop-blur-sm flex items-center justify-center gap-1.5 overflow-x-auto shrink-0">
          {/* Collection Dropdown */}
          <div className="relative flex-shrink-0">
            <button
              ref={collectionButtonRef}
              onClick={() => setShowCollectionSelector(!showCollectionSelector)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                showCollectionSelector 
                  ? 'bg-gray-200 text-gray-800' 
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
              title="Add to or change collection"
            >
              <FolderOpen size={13} />
              <span>Collection</span>
              <ChevronDown size={12} className={`transition-transform ${showCollectionSelector ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Collection Dropdown Menu - Use portal to render above everything */}
            {typeof document !== 'undefined' && createPortal(
              showCollectionSelector && (
                <>
                  {/* Backdrop to close dropdown */}
                  <div
                    className="fixed inset-0"
                    style={{ zIndex: 9998, backgroundColor: 'rgba(0, 0, 0, 0.01)' }}
                    onClick={() => setShowCollectionSelector(false)}
                  />
                  <div 
                    className="fixed w-56 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
                    style={{ 
                      zIndex: 9999,
                      top: collectionButtonRef.current ? `${collectionButtonRef.current.getBoundingClientRect().top - 8}px` : 'auto',
                      left: collectionButtonRef.current ? `${collectionButtonRef.current.getBoundingClientRect().left}px` : 'auto',
                      transform: 'translateY(-100%)',
                    }}
                  >
                    <div className="max-h-64 overflow-y-auto">
                      {collections.length > 0 ? (
                        collections.map((collection) => (
                        <button
                          key={collection.id}
                          onClick={() => {
                            handleChangeCollection(collection.id);
                            setShowCollectionSelector(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors flex items-center justify-between ${
                            (currentCollection?.id || currentContent?.meta?.collection_id) === collection.id 
                              ? 'bg-purple-50 text-purple-700' 
                              : 'text-gray-700'
                          }`}
                        >
                          <span>{collection.name}</span>
                          {(currentCollection?.id || currentContent?.meta?.collection_id) === collection.id && (
                            <Check size={14} className="text-purple-600" />
                          )}
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500 text-center">
                        No collections available
                      </div>
                    )}
                    </div>
                    {changingCollection && (
                      <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-500">
                        <div className="w-3 h-3 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                        Saving...
                      </div>
                    )}
                  </div>
                </>
              ),
              document.body
            )}
          </div>

          {/* Ask Swirl - Navigate to AI Chat page with item as chip */}
          <button
            onClick={() => {
              // Clear existing chips and add this item as the selected context
              clearChips();
              addChip({
                id: currentContent.id,
                type: 'item',
                label: currentContent.title || currentContent.custom_title || 'Item',
                thumbnail: currentContent.thumbnail_url || currentContent.image_url || currentContent.url,
                file_url: currentContent.url || currentContent.image_url,
                file_name: currentContent.title || 'file',
                file_type: currentContent.source_type || 'unknown',
                source: 'swirlspace',
              });
              onClose();
              navigate('/ai-chat');
            }}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium bg-gradient-to-r from-purple-600 to-purple-500 text-white hover:from-purple-700 hover:to-purple-600 transition-all shadow-sm whitespace-nowrap flex-shrink-0"
            title="Ask Swirl AI about this content"
          >
            <Sparkles size={13} />
            <span>Ask AI</span>
          </button>

          {/* Add to Focus */}
          <button
            onClick={() => {
              // TODO: Implement Add to Focus functionality
              console.log('Add to Focus:', currentContent.id);
            }}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium bg-white text-gray-600 hover:bg-gray-100 border border-gray-200 transition-all whitespace-nowrap flex-shrink-0"
            title="Add to your Focus workspace"
          >
            <Focus size={13} />
            <span>Focus</span>
          </button>

          {/* Download */}
          <button
            onClick={() => {
              const url = currentContent.url || currentContent.image_url;
              if (url) {
                // For images, try to download with proper filename
                fetch(url)
                  .then(response => response.blob())
                  .then(blob => {
                    const blobUrl = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = blobUrl;
                    a.download = currentContent.title || 'download';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(blobUrl);
                  })
                  .catch(() => {
                    // Fallback: open in new tab
                    window.open(url, '_blank');
                  });
              }
            }}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium bg-white text-gray-600 hover:bg-gray-100 border border-gray-200 transition-all whitespace-nowrap flex-shrink-0"
            title="Download this content"
          >
            <Download size={13} />
            <span>Download</span>
          </button>

          {/* Reading Mode (for articles) */}
          {(currentContent.source_type === 'article' || 
            currentContent.source_type === 'link' || 
            currentContent.source_type === 'web_clip' ||
            currentContent.meta?.article_html ||
            currentContent.meta?.archived_html) && 
            (currentContent.raw_text || currentContent.content || currentContent.meta?.article_html) && (
            <button
              onClick={() => setShowReadingMode(true)}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium bg-purple-50 text-purple-600 hover:bg-purple-100 border border-purple-200 transition-all whitespace-nowrap flex-shrink-0"
              title="Open in reading mode"
            >
              <BookOpen size={13} />
              <span>Read</span>
            </button>
          )}

          {/* Copy Link */}
          <button
            onClick={() => {
              const shareUrl = currentContent.url || window.location.href;
              navigator.clipboard.writeText(shareUrl);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${
              copied 
                ? 'bg-green-50 text-green-600 border border-green-200' 
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
            title="Copy the link to this content"
          >
            {copied ? <Check size={13} /> : <Link2 size={13} />}
            <span>{copied ? 'Copied' : 'Copy'}</span>
          </button>
        </div>
      </div>
      </div>
    </div>
              
      {/* Full Screen Image Viewer */}
      {fullScreenImage && (
        <div 
          className="fixed inset-0 bg-black/95 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={() => setFullScreenImage(null)}
        >
                <button
            onClick={() => setFullScreenImage(null)}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full w-10 h-10 flex items-center justify-center cursor-pointer transition-colors z-10"
            title="Close"
                >
            <X size={24} className="text-white" />
                </button>
          <div 
            className="max-w-[95vw] max-h-[95vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={fullScreenImage}
              alt={currentContent.title || 'Full image'}
              className="max-w-full max-h-[95vh] object-contain rounded-lg shadow-2xl"
              style={{ borderRadius: '8px' }}
              onError={(e) => {
                console.error('Failed to load full screen image:', fullScreenImage);
                setFullScreenImage(null);
              }}
            />
          </div>
            </div>
          )}

      {/* Reading Mode Modal */}
      <ReadingModeModal
        isOpen={showReadingMode}
        onClose={() => setShowReadingMode(false)}
        article={currentContent}
      />
    </>
  );
};

// Old RelatedItemCard removed - now using component from ../related-items/RelatedItemCard.jsx

export default ContentDetailModal;
